import {cerrarSession, getTokenUser, getUser, setTokenUser, setUserSession} from "../common/Common";
import {endpoints} from "./endpoints";

const REFRESH_AHEAD_SECONDS = Number(process.env.REACT_APP_REFRESH_AHEAD_SECONDS || '60');
const AUTH_DEBUG = String(process.env.REACT_APP_AUTH_DEBUG || '').toLowerCase() === 'true';
let refreshInFlight = null;

const authLog = (...args) => {
    if (AUTH_DEBUG) {
        console.log('[auth]', ...args);
    }
};

const debugLog = (...args) => {
    if (AUTH_DEBUG) {
        console.log(...args);
    }
};

const decodeJwtPayload = (token) => {
    if (!token || typeof token !== 'string') return null;
    const parts = token.split('.');
    if (parts.length < 2) return null;

    try {
        const normalized = parts[1].replace(/-/g, '+').replace(/_/g, '/');
        const padded = normalized + '='.repeat((4 - (normalized.length % 4)) % 4);
        return JSON.parse(atob(padded));
    } catch (e) {
        return null;
    }
};

const secondsToExpiry = (token) => {
    const payload = decodeJwtPayload(token);
    if (!payload || typeof payload.exp !== 'number') return null;
    return payload.exp - Math.floor(Date.now() / 1000);
};

const isExpired = (token) => {
    const seconds = secondsToExpiry(token);
    return seconds != null && seconds <= 0;
};

const shouldRefreshSoon = (token) => {
    const seconds = secondsToExpiry(token);
    return seconds != null && seconds <= REFRESH_AHEAD_SECONDS;
};

// Marca la sesion como expirada SOLO si todavia hay una sesion activa.
// Si el usuario ya cerro sesion (o nunca la tuvo), una respuesta 401 tardia de una
// peticion en vuelo no debe dejar el aviso "sesion expirada" para el siguiente login.
const expirarSesion = () => {
    if (!getTokenUser()?.accessToken) {
        authLog('401 ignorado: no hay sesion activa');
        return;
    }
    cerrarSession('expired');
};

const refreshAccessToken = async () => {
    if (refreshInFlight) return refreshInFlight;

    refreshInFlight = (async () => {
        const tokens = getTokenUser();
        if (!tokens?.refreshToken) {
            authLog('refresh cancelado: no existe refreshToken');
            throw new Error('No hay refresh token disponible');
        }

        if (isExpired(tokens.refreshToken)) {
            authLog('refresh cancelado: refreshToken expirado');
            expirarSesion();
            throw new Error('Refresh token expirado');
        }
        authLog('iniciando refresh contra backend');
        const tokenData = await fetchAPI(endpoints.JWTRefresh, 'POST', null, {
            skipPreRefresh: true,
            retryAfterRefresh: true,
        });
        authLog('refresh exitoso: tokens actualizados');
        setTokenUser(tokenData);
        setUserSession(getUser());
        return tokenData;
    })().finally(() => {
        authLog('refresh finalizado');
        refreshInFlight = null;
    });

    return refreshInFlight;
};

export default async function fetchAPI(url, method = 'GET', bodyParam, options = {}) {
    let errorSW, data
    const arrayErrores = mensajes()
    try {
        const isRefreshRequest = url.includes(endpoints.JWTRefresh);
        let sessionParams = getTokenUser();

        if (!isRefreshRequest && sessionParams?.accessToken && sessionParams?.refreshToken && !options.skipPreRefresh) {
            const accessSecondsLeft = secondsToExpiry(sessionParams.accessToken);
            authLog('pre-check token', {
                url,
                method,
                accessSecondsLeft,
                refreshAheadSeconds: REFRESH_AHEAD_SECONDS,
            });

            if (shouldRefreshSoon(sessionParams.accessToken)) {
                authLog('pre-refresh disparado por expiracion cercana');
                await refreshAccessToken();
                sessionParams = getTokenUser();
            }
        }

        const headers = {
            Accept: 'application/json',
            'Content-Type': 'application/json'
        }

        if (sessionParams && sessionParams.accessToken) {
            if (!isRefreshRequest) {
                headers.Authorization = `Bearer ${sessionParams.accessToken}`
            } else {
                headers.Authorization = `Bearer ${sessionParams.refreshToken}`
            }
        }
        const body = bodyParam ? JSON.stringify(bodyParam) : null

        debugLog('====method====' + method)
        debugLog('====headers====' + JSON.stringify(headers))
        debugLog('====URL====' + url)
        debugLog('====body====' + body)

        const res = await fetch(url, {
            method,
            headers,
            body
        })
        const contentType = res.headers.get('Content-Type');

        if (contentType && contentType.includes('application/x-pdf')) {
            const data = await res.blob();
            const blob = new Blob([data], {type: "application/pdf"});
            const url = URL.createObjectURL(blob);
            return {
                type: 'pdf',
                data: url
            };
        } else {
            const raw = await res.text();
            if (!raw) {
                data = null;
            } else {
                try {
                    data = JSON.parse(raw);
                } catch (parseError) {
                    data = {message: raw};
                }
            }
        }

        debugLog('====RESPONSE RES====' + JSON.stringify(data))

        const responseMessage = data && typeof data === 'object' ? data.message : data;

        if (res.status < 200 || res.status >= 300) {
            if (res.status === 401 && sessionParams?.accessToken && !isRefreshRequest && !options.retryAfterRefresh) {
                authLog('401 detectado, intentando refresh y retry', { url, method });
                try {
                    await refreshAccessToken();
                    authLog('retry de request original tras refresh', { url, method });
                    return await fetchAPI(url, method, bodyParam, {
                        ...options,
                        skipPreRefresh: true,
                        retryAfterRefresh: true,
                    });
                } catch (refreshError) {
                    authLog('refresh/retry fallo, cerrando sesion', { reason: refreshError?.message || 'unknown' });
                    errorSW = generaError('Su sesión ha expirado', 'expired_session', res, res.status);
                    expirarSesion();
                }
            }

            let estado = res.status;

            if (!errorSW && isRefreshRequest && (estado === 401 || estado === 403)) {
                authLog('refresh respondio no autorizado', { estado });
                try {
                    errorSW = generaError('Su sesión ha expirado', 'expired_session', res, res.status);
                    expirarSesion();
                } catch (e) {
                    console.error('Error cerrando sesión tras expiración de refresh', e);
                }
            } else if (!errorSW && estado === 401) {
                errorSW = generaError(validarMensaje(responseMessage, url), estado, res, res.status);
                // el login con credenciales incorrectas tambien responde 401: no es una sesion expirada
                if (!url.includes(endpoints.login)) {
                    expirarSesion();
                }
            } else if (!errorSW && estado === 403) {
                errorSW = generaError(validarMensaje(responseMessage, url), estado, res, res.status);
            } else if (!errorSW) {
                errorSW = generaError(validarMensaje(responseMessage, url), estado, res, res.status);
            }
        }
    } catch (error) {
        if (error.code === -100) {
            errorSW = generaError('Su sesión ha expirado', 'expired_session');
            cerrarSession();
        } else {
            let mensajeError = '';
            if (error.message) {
                arrayErrores.forEach(element => {
                    if (error.message.includes(element['message']) ||
                        error.message === element['message']) {
                        mensajeError = element['mensaje'];
                    }
                });
            } else {
                mensajeError = arrayErrores[0]['mensaje'];
            }
            let detError = mensajeError;
            if (mensajeError === '') {
                detError = error.message;
            }
            errorSW = generaError(detError, 'network_error');
        }
    }
    if (!errorSW) {
        return data
    }
    throw errorSW
}

// fetchAPIraw: similar a fetchAPI pero devuelve el Response sin parsear.
// Útil para descargas (blob) y para acceder a headers.
export async function fetchAPIraw(url, method = 'GET', bodyParam = null, options = {}) {
    try {
        const headers = {
            Accept: options.accept || '*/*',
        };
        const sessionParams = getTokenUser();
        if (sessionParams && sessionParams.accessToken) {
            headers.Authorization = `Bearer ${sessionParams.accessToken}`;
        }

        let body = null;
        if (bodyParam != null) {
            if (options.form) {
                // bodyParam expected to be an object or URLSearchParams
                body = (bodyParam instanceof URLSearchParams) ? bodyParam.toString() : new URLSearchParams(bodyParam).toString();
                headers['Content-Type'] = 'application/x-www-form-urlencoded';
            } else if (options.raw) {
                body = bodyParam;
                if (options.contentType) headers['Content-Type'] = options.contentType;
            } else {
                body = JSON.stringify(bodyParam);
                headers['Content-Type'] = 'application/json';
            }
        }

        const res = await fetch(url, {
            method,
            headers,
            body,
        });

        return res;
    } catch (error) {
        throw error;
    }
}

//******* GENERA ERROR
const generaError = (mensaje, code, res, status) => {
    let errorSW;
    let msjPantalla = "Se ha generado un error en el sistema, notifique al administrador";
    if (mensaje) {
        msjPantalla = mensaje;
    }
    errorSW = new Error(msjPantalla);
    errorSW.res = res;
    errorSW.status = status;
    errorSW.code = code;
    return errorSW;
};

//******* VALIDA MENSAJES
const validarMensaje = (mensajeSW, url) => {
    let unexError = `Unexpected Error (${url})`;
    if (mensajeSW != null) {
        unexError = mensajeSW.replace(/<br>/g, '. ').replace(/<br\/>/g, '. ');
    }
    return (unexError);
};

//******* MENSAJES DE ERROR
const mensajes = () => {
    const arrayErrores = [
        {
            message: 'Network request failed',
            mensaje: 'Error de la red, por favor verifique la conexión'
        },
        {
            message: 'Unexpected token',
            mensaje: 'Se produjo un error al realizar la operación'
        },
        {
            message: 'Unrecognized token',
            mensaje:
                'Se produjo un error al realizar la operación, verifique la conexión'
        },
        {
            message: 'JSON parse error',
            mensaje:
                'Se produjo un error al realizar la operación, verifique la conexión'
        },
        {
            message: 'Unexpected end of JSON input',
            mensaje: 'Error de la red, por favor verifique la conexión'
        },
        {
            message: 'Failed to fetch',
            mensaje: 'Error de la red, por favor verifique la conexión'
        }
    ]
    return arrayErrores
}
